import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOW,
} from 'styles/tokens';

interface ToolChoiceSelectProps {
  value: string;
  onChange: (value: string) => void;
  functionNames: string[];
}

const BASE_OPTIONS = ['auto', 'none', 'required'];

const glassMenuProps = {
  sx: {
    '& .MuiPaper-root': {
      mt: 0.5,
      borderRadius: RADIUS_CARD,
      background: GLASS_BACKGROUND,
      backdropFilter: GLASS_BLUR,
      boxShadow: SHADOW,
    },
  },
};

/**
 * Tool-choice picker. `auto`/`none`/`required` plus one option per available function.
 * Disabled (and pinned to `auto`) when there are no functions, since the backend ignores
 * toolChoice without tools.
 */
const ToolChoiceSelect: FC<ToolChoiceSelectProps> = ({ value, onChange, functionNames }) => {
  const disabled = functionNames.length === 0;

  return (
    <Select
      value={disabled ? 'auto' : value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      size="small"
      MenuProps={glassMenuProps}
      sx={{
        minWidth: 150,
        borderRadius: RADIUS_ITEM,
        bgcolor: SURFACE_CONTAINER_LOW,
        fontFamily: 'Jost, sans-serif',
        fontSize: 13,
        color: NAVY,
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
      }}
    >
      {BASE_OPTIONS.map((opt) => (
        <MenuItem key={opt} value={opt} sx={{ fontFamily: 'Jost, sans-serif', fontSize: 13 }}>
          {opt}
        </MenuItem>
      ))}
      {functionNames.length > 0 && (
        <ListSubheader
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: alpha(NAVY, 0.5),
            background: 'transparent',
            lineHeight: '28px',
          }}
        >
          Forzar función
        </ListSubheader>
      )}
      {functionNames.map((name) => (
        <MenuItem key={name} value={name} sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }}>
          <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5, color: NAVY }}>{name}</Typography>
        </MenuItem>
      ))}
    </Select>
  );
};

export default ToolChoiceSelect;
